// App-side delegate for `@heidi/commerce/data` (close-out, ARCFE-979). The
// factory moved whole — it was already port-shaped. This file keeps the name,
// the config signature its one consumer (`useMutateActivateClinicianTrial`,
// app-side by design) passes, and every user-visible effect:
//   - the destructive toast + intl, with `getActivateTrialErrorDescription`'s
//     `AxiosError` shape-check beside it (naming axios stays out of the
//     package surface).
//   - `invalidateCurrentUser` — workspace's invalidator, supplied as the
//     `refreshCurrentUser` port so the package keeps the delayed refresh's
//     Promise.all concurrency.
//   - the billing-snapshot write, supplied via the app wrapper that reads the
//     affiliate code (the D3 layering).
import { logger } from '@/utils/logger/logger';
import { type ActivateClinicianTrialResponse } from '@heidi/commerce/contracts';
import { useMutateActivateTrial as useMutateActivateTrialCore } from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import type { components } from '@heidi/schema/ml-scribe';
import { AxiosError } from 'axios';
import { useIntl } from 'react-intl';
import { invalidateCurrentUser } from '../query/useQueryUser';
import { setUserBillingInfoQueryCacheFromBillingInfo } from '../query/useQueryUserBillingInfo';

type ErrorResponse = components['schemas']['ErrorResponse'];

type ActivateTrialResponse = { data?: ActivateClinicianTrialResponse };

type ActivateTrialMutationConfig = {
  mutationKey: string;
  activate: () => Promise<ActivateTrialResponse>;
  errorTitle: { id: string; defaultMessage: string };
  refreshLogContext: string;
  afterSuccess?: (data: ActivateClinicianTrialResponse) => void | Promise<void>;
};

function getActivateTrialErrorDescription(error: unknown): string {
  if (error instanceof AxiosError) {
    return (
      (error.response?.data as ErrorResponse | undefined)?.detail?.msg ?? error.message
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

function useMutateActivateTrial({
  mutationKey,
  activate,
  errorTitle,
  refreshLogContext,
  afterSuccess,
}: ActivateTrialMutationConfig) {
  const intl = useIntl();

  return useMutateActivateTrialCore({
    mutationKey,
    activate,
    refreshLogContext,
    afterSuccess,
    applyBillingSnapshot: setUserBillingInfoQueryCacheFromBillingInfo,
    refreshCurrentUser: invalidateCurrentUser,
    onActivateError: (error) => {
      toast({
        tone: 'destructive',
        title: intl.formatMessage(errorTitle),
        description: getActivateTrialErrorDescription(error),
      });
      logger.error(intl.formatMessage(errorTitle), error);
    },
  });
}

export { useMutateActivateTrial };
